import React from 'react';
import { Wallet, CreditCard, TrendingUp, Shield, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/user/Navbar';
import Footer from '../../../components/user/Footer';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const DigitalWalletSystem: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [featuresRef, featuresVisible] = useScrollAnimation();
  const [benefitsRef, benefitsVisible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 to-white">
        <div className={`max-w-7xl mx-auto transition-all duration-700 ${
          heroVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <Link 
            to="/services" 
            className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Services
          </Link>
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-primary-100 p-4 rounded-2xl">
              <Wallet className="text-primary-600" size={64} />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-secondary-900 mb-4 tracking-tight">
                Digital Wallet System
              </h1>
              <p className="text-xl text-secondary-600">
                Complete wallet solution for both customers and businesses
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-secondary-900 mb-6">
                Manage Funds Seamlessly
              </h2>
              <p className="text-lg text-secondary-600 leading-relaxed mb-6">
                Our Digital Wallet System provides a comprehensive solution for managing funds, 
                tracking transactions, and processing settlements. Whether you're a customer 
                making payments or a business receiving them, our wallet system simplifies 
                financial management.
              </p>
              <p className="text-lg text-secondary-600 leading-relaxed">
                Add money via secure payment gateways, track all transactions in real-time, 
                and manage your balance with ease. For businesses, our settlement management 
                system ensures timely and secure transfers to your bank account.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-12 text-white shadow-soft">
              <Shield size={48} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Secure & Protected</h3>
              <p className="text-lg text-white/90 leading-relaxed">
                Your funds are protected by bank-level security. All transactions are encrypted 
                and monitored 24/7 to ensure complete safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 px-4 bg-secondary-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            featuresVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-secondary-600">
              Everything you need for digital wallet management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Wallet size={40} />,
                title: 'User & Business Wallets',
                description: 'Separate wallet systems for customers and businesses. Each with features tailored to their specific needs.'
              },
              {
                icon: <CreditCard size={40} />,
                title: 'Instant Transfers',
                description: 'Transfer funds instantly between wallets. Make payments and receive settlements without delays.'
              },
              {
                icon: <TrendingUp size={40} />,
                title: 'Settlement Management',
                description: 'For businesses: Request settlements, track withdrawal status, and manage funds with ease.'
              },
              {
                icon: <CheckCircle size={40} />,
                title: 'Transaction History',
                description: 'Complete transaction records with detailed information. Filter, search, and export your transaction data.'
              },
              {
                icon: <Shield size={40} />,
                title: 'Secure Top-ups',
                description: 'Add money securely via Stripe and other payment gateways. Multiple payment methods supported.'
              },
              {
                icon: <Wallet size={40} />,
                title: 'Balance Tracking',
                description: 'Real-time balance updates. Always know your current balance and available funds.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 hover:shadow-soft transition-all duration-500 group hover:scale-105 hover:-translate-y-2 ${
                  featuresVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 group-hover:text-primary-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-secondary-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section ref={benefitsRef} className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-700 ${
            benefitsVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
          }`}>
            <h2 className="text-4xl font-bold text-secondary-900 mb-4">
              Why Choose Our Digital Wallet?
            </h2>
            <p className="text-xl text-secondary-600">
              Benefits that make financial management easy
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Convenience',
                description: 'Manage all your payments in one place. No need for multiple apps or services.'
              },
              {
                title: 'Fast Transactions',
                description: 'Instant transfers and payments. No waiting periods or processing delays.'
              },
              {
                title: 'Transparent Fees',
                description: 'Clear and transparent fee structure. Know exactly what you\'re paying for.'
              },
              {
                title: '24/7 Access',
                description: 'Access your wallet anytime, anywhere. Check balances and make transactions 24/7.'
              },
              {
                title: 'Multiple Payment Methods',
                description: 'Add money via credit cards, debit cards, bank transfers, and more.'
              },
              {
                title: 'Business Settlements',
                description: 'For businesses: Request settlements on your schedule. Instant or scheduled withdrawals available.'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className={`bg-white p-8 rounded-xl border-2 border-secondary-100 hover:border-primary-500 transition-all duration-500 ${
                  benefitsVisible 
                    ? `animate-fade-in-up opacity-100` 
                    : 'opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h3 className="text-xl font-bold text-secondary-900 mb-3 flex items-center">
                  <CheckCircle className="text-primary-500 mr-3" size={24} />
                  {benefit.title}
                </h3>
                <p className="text-secondary-600">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-4 bg-primary-500">
        <div className={`max-w-4xl mx-auto text-center text-white transition-all duration-700 ${
          ctaVisible ? 'animate-fade-in-up opacity-100' : 'opacity-0'
        }`}>
          <h2 className="text-4xl font-bold mb-6 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Start using our digital wallet system today
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-primary-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-secondary-50 transition-all duration-300 shadow-xl hover:scale-110 hover:-translate-y-1"
            >
              Contact Sales
            </Link>
            <Link
              to="/services"
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary-600 transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalWalletSystem;

